import cn from 'classnames';
import { ChangeEvent, FC, useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { FormattedMessage, useIntl } from 'react-intl';
import { useSearchParams } from 'react-router';

import { PatientsListColumnName } from '@/shared/config';
import {
  formatDateToYYYYMMDD,
  getDateRange,
  parseDateAsLocal,
  trackEvent,
} from '@/shared/lib';
import { Button, DatePicker, Icon, RadioGroup } from '@/shared/ui';

import { ColumnValues } from '@/pages/Patients/config/i18n';

import { DATE_RANGE_OPTIONS, MENU_OFFSET } from '../config/constants';
import { lastModifiedFilterMessages } from '../config/i18n';
import { DateRangeOption } from '../config/types';

import styles from './LastModifiedDropdownFilter.module.scss';

type LastModifiedDropdownFilterProps = {
  className?: string;
  testId?: string;
  onChange?: (dates: { from?: Date; to?: Date }) => void;
};

type FilterState = {
  period: DateRangeOption;
  from?: Date;
  to?: Date;
};

export const LastModifiedDropdownFilter: FC<
  LastModifiedDropdownFilterProps
> = ({ className, testId, onChange }) => {
  const { formatMessage, locale } = useIntl();
  const [searchParams, setSearchParams] = useSearchParams();
  const triggerRef = useRef<HTMLButtonElement>(null);
  const [menuPosition, setMenuPosition] = useState<{
    top: number;
    right: number;
  } | null>(null);

  const initialPeriod =
    (searchParams.get('lastModifiedPeriod') as DateRangeOption) ||
    DATE_RANGE_OPTIONS.ALL;
  const initialFrom = searchParams.get('lastModifiedFrom');
  const initialTo = searchParams.get('lastModifiedTo');

  const [isOpen, setIsOpen] = useState(false);
  const [selectedPeriod, setSelectedPeriod] =
    useState<DateRangeOption>(initialPeriod);
  const [customFrom, setCustomFrom] = useState<Date | undefined>(
    initialFrom ? parseDateAsLocal(initialFrom) : undefined,
  );
  const [customTo, setCustomTo] = useState<Date | undefined>(
    initialTo ? parseDateAsLocal(initialTo) : undefined,
  );

  const [initialFilterState, setInitialFilterState] = useState<FilterState>({
    period: initialPeriod,
    from: initialFrom ? parseDateAsLocal(initialFrom) : undefined,
    to: initialTo ? parseDateAsLocal(initialTo) : undefined,
  });

  const periodOptions = useMemo(
    () => [
      {
        value: DATE_RANGE_OPTIONS.ALL,
        label: formatMessage(lastModifiedFilterMessages.all),
      },
      {
        value: DATE_RANGE_OPTIONS.TODAY,
        label: formatMessage(lastModifiedFilterMessages.today),
      },
      {
        value: DATE_RANGE_OPTIONS.ONE_WEEK,
        label: formatMessage(lastModifiedFilterMessages.oneWeek),
      },
      {
        value: DATE_RANGE_OPTIONS.CUSTOM,
        label: formatMessage(lastModifiedFilterMessages.custom),
      },
    ],
    [formatMessage],
  );

  const hasActiveFilter = selectedPeriod !== DATE_RANGE_OPTIONS.ALL;

  const handlePeriodChange = (e: ChangeEvent<HTMLInputElement>) => {
    setSelectedPeriod(e.currentTarget.value as DateRangeOption);
  };

  const handleApply = () => {
    const dateRange =
      selectedPeriod === DATE_RANGE_OPTIONS.CUSTOM
        ? { from: customFrom, to: customTo }
        : getDateRange(selectedPeriod);

    trackEvent({
      eventName: 'PatientsPageLastModifiedFilterApplyButtonClicked',
      properties: { period: selectedPeriod },
    });

    if (onChange) {
      onChange(dateRange);
    }

    if (selectedPeriod === DATE_RANGE_OPTIONS.ALL) {
      searchParams.delete('lastModifiedPeriod');
      searchParams.delete('lastModifiedFrom');
      searchParams.delete('lastModifiedTo');
    } else {
      searchParams.set('lastModifiedPeriod', selectedPeriod);
      if (dateRange.from) {
        searchParams.set(
          'lastModifiedFrom',
          formatDateToYYYYMMDD(dateRange.from),
        );
      } else {
        searchParams.delete('lastModifiedFrom');
      }
      if (dateRange.to) {
        searchParams.set('lastModifiedTo', formatDateToYYYYMMDD(dateRange.to));
      } else {
        searchParams.delete('lastModifiedTo');
      }
    }
    setSearchParams(searchParams);

    setIsOpen(false);
  };

  const handleClear = () => {
    setSelectedPeriod(DATE_RANGE_OPTIONS.ALL);
    setCustomFrom(undefined);
    setCustomTo(undefined);

    if (onChange) {
      onChange({ from: undefined, to: undefined });
    }

    searchParams.delete('lastModifiedPeriod');
    searchParams.delete('lastModifiedFrom');
    searchParams.delete('lastModifiedTo');
    setSearchParams(searchParams);

    setIsOpen(false);
  };

  const handleCancel = () => {
    setSelectedPeriod(initialFilterState.period);
    setCustomFrom(initialFilterState.from);
    setCustomTo(initialFilterState.to);
    setIsOpen(false);
  };

  useEffect(() => {
    if (isOpen) {
      setInitialFilterState({
        period: selectedPeriod,
        from: customFrom,
        to: customTo,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  const handleOpenMenu = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.stopPropagation();

    if (triggerRef.current) {
      const rect = triggerRef.current.getBoundingClientRect();
      setMenuPosition({
        top: rect.bottom + MENU_OFFSET,
        right: window.innerWidth - rect.right,
      });
    }

    setIsOpen(true);
  };

  return (
    <div
      className={cn(styles.container, className)}
      data-testid={testId}
      onClick={(e) => e.stopPropagation()}
    >
      <button
        ref={triggerRef}
        className={cn(styles.trigger, 'p2', isOpen && styles.isOpen)}
        type="button"
        onClick={handleOpenMenu}
      >
        {formatMessage(ColumnValues[PatientsListColumnName.LastModified])}

        {hasActiveFilter && <span className={cn('h6', styles.badge)}>1</span>}

        <Icon className={styles.filter} name="filter" size={18} />
      </button>

      {isOpen &&
        menuPosition &&
        createPortal(
          <>
            <div className={styles.overlay} onClick={() => handleCancel()} />
            <div
              className={styles.menu}
              style={{
                top: `${menuPosition.top}px`,
                right: `${menuPosition.right}px`,
              }}
            >
              <div className={styles.content}>
                <RadioGroup
                  name="lastModifiedPeriod"
                  variant="default"
                  value={selectedPeriod}
                  items={periodOptions}
                  onChange={handlePeriodChange}
                  className={styles.radioGroup}
                />

                {selectedPeriod === DATE_RANGE_OPTIONS.CUSTOM && (
                  <div className={styles.dateRangeContainer}>
                    <DatePicker
                      locale={locale}
                      label={formatMessage(lastModifiedFilterMessages.from)}
                      value={customFrom}
                      onChange={(val) => setCustomFrom(val || undefined)}
                      maxDate={customTo || new Date()}
                      className={styles.datePicker}
                      withPortal
                    />
                    <DatePicker
                      locale={locale}
                      label={formatMessage(lastModifiedFilterMessages.to)}
                      value={customTo}
                      onChange={(val) => setCustomTo(val || undefined)}
                      minDate={customFrom}
                      maxDate={new Date()}
                      className={styles.datePicker}
                      withPortal
                    />
                  </div>
                )}
              </div>

              <div className={styles.divider} />

              <div className={styles.actions}>
                <Button
                  variant="tertiary"
                  size="medium"
                  className={styles.clearButton}
                  onClick={handleClear}
                >
                  <FormattedMessage id="global.clear" defaultMessage="Clear" />
                </Button>

                <Button
                  variant="primary"
                  size="medium"
                  className={styles.applyButton}
                  onClick={handleApply}
                >
                  <FormattedMessage id="global.apply" defaultMessage="Apply" />
                </Button>
              </div>
            </div>
          </>,
          document.body,
        )}
    </div>
  );
};
